import React from 'react';
import { LAPSE_THRESHOLD_MS } from '../../config/testMetrics';

export interface TestResultsPoint {
  label: string;
  average: number | null;
  median?: number | null;
}

interface TestResultsChartProps {
  data: TestResultsPoint[];
  showXAxisLabels?: boolean;
  yMin?: number;
  yMax?: number;
}

const CHART_WIDTH = 640;
const CHART_HEIGHT = 260;
const PADDING = 40;

const buildPathWithGaps = (points: Array<{ x: number; y: number; valid: boolean }>) => {
  if (points.length === 0) return '';
  let path = '';
  points.forEach((point) => {
    if (!point.valid) return;
    if (!path) {
      path = `M ${point.x} ${point.y}`;
      return;
    }
    path += ` L ${point.x} ${point.y}`;
  });
  return path;
};

export function TestResultsChart({
  data,
  showXAxisLabels = true,
  yMin,
  yMax,
}: TestResultsChartProps) {
  if (!data.length) {
    return (
      <div className="bg-navy-800 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-white">Reaction-time trend</h2>
        </div>
        <div className="py-12 text-center text-sm text-navy-300">No completed tests available yet.</div>
      </div>
    );
  }

  const values = data
    .flatMap((point) => [point.average, point.median])
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));

  let minValue: number;
  let maxValue: number;
  if (typeof yMin === 'number' && typeof yMax === 'number') {
    minValue = yMin;
    maxValue = yMax;
  } else {
    const bounds = values.length ? [...values, LAPSE_THRESHOLD_MS] : [195, 445];
    const dataMin = Math.min(...bounds);
    const dataMax = Math.max(...bounds);
    const padding = Math.max(20, (dataMax - dataMin) * 0.15);
    minValue = Math.max(0, Math.floor((dataMin - padding) / 10) * 10);
    maxValue = Math.ceil((dataMax + padding) / 10) * 10;
    if (maxValue - minValue < 50) {
      const mid = (maxValue + minValue) / 2;
      minValue = Math.max(0, Math.floor((mid - 25) / 10) * 10);
      maxValue = minValue + 50;
    }
  }
  const range = maxValue - minValue || 1;

  const mapX = (index: number) => {
    if (data.length === 1) return CHART_WIDTH / 2;
    return PADDING + (index / (data.length - 1)) * (CHART_WIDTH - PADDING * 2);
  };
  const mapY = (value: number) => {
    const clamped = Math.min(Math.max(value, minValue), maxValue);
    return CHART_HEIGHT - PADDING - ((clamped - minValue) / range) * (CHART_HEIGHT - PADDING * 2);
  };

  const avgPoints = data.map((point, index) => {
    const value = point.average;
    const valid = typeof value === 'number' && Number.isFinite(value);
    return { x: mapX(index), y: mapY(valid ? value! : minValue), valid };
  });
  const medPoints = data.map((point, index) => {
    const value = point.median ?? point.average ?? null;
    const valid = typeof value === 'number' && Number.isFinite(value);
    return { x: mapX(index), y: mapY(valid ? value! : minValue), valid };
  });

  const avgPath = buildPathWithGaps(avgPoints);
  const medPath = buildPathWithGaps(medPoints);

  const tickStep = Math.max(1, Math.floor(data.length / 6));
  const thresholdY = mapY(LAPSE_THRESHOLD_MS);

  return (
    <div className="bg-navy-800 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-white">Reaction-time trend</h2>
      </div>

      <div className="flex items-center gap-6 mb-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-8 h-0.5 bg-gold-500"></div>
          <span className="text-navy-200">Average RT</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-0.5 border-t-2 border-dashed border-gold-500"></div>
          <span className="text-navy-200">Median RT</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-0.5 border-t-2 border-dotted border-red-400"></div>
          <span className="text-navy-200">Lapse threshold ({LAPSE_THRESHOLD_MS} ms)</span>
        </div>
      </div>

      <svg viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`} className="w-full h-72 mt-6">
        <rect x={0} y={0} width={CHART_WIDTH} height={CHART_HEIGHT} rx={16} fill="#13214A" />

        {Array.from({ length: 5 }).map((_, idx) => {
          const y = PADDING + (idx / 4) * (CHART_HEIGHT - PADDING * 2);
          return (
            <line
              key={`grid-${idx}`}
              x1={PADDING}
              y1={y}
              x2={CHART_WIDTH - PADDING}
              y2={y}
              stroke="#28396B"
              strokeWidth="1"
            />
          );
        })}

        <line x1={PADDING} y1={PADDING} x2={PADDING} y2={CHART_HEIGHT - PADDING} stroke="#93A0C4" strokeWidth="1.5" />
        <line x1={PADDING} y1={CHART_HEIGHT - PADDING} x2={CHART_WIDTH - PADDING} y2={CHART_HEIGHT - PADDING} stroke="#93A0C4" strokeWidth="1.5" />

        {/* Lapse threshold reference line */}
        <line
          x1={PADDING}
          y1={thresholdY}
          x2={CHART_WIDTH - PADDING}
          y2={thresholdY}
          stroke="#F87171"
          strokeWidth="1.5"
          strokeDasharray="2 4"
        />
        <text x={CHART_WIDTH - PADDING + 4} y={thresholdY + 4} textAnchor="start" fontSize="10" fill="#F87171">
          {LAPSE_THRESHOLD_MS} ms
        </text>

        {Array.from({ length: 5 }).map((_, idx) => {
          const value = maxValue - (idx / 4) * range;
          const y = PADDING + (idx / 4) * (CHART_HEIGHT - PADDING * 2);
          return (
            <text
              key={`y-${idx}`}
              x={PADDING - 8}
              y={y + 4}
              textAnchor="end"
              fontSize="11"
              fill="#93A0C4"
            >
              {Math.round(value)}
            </text>
          );
        })}

        {showXAxisLabels &&
          data.map((point, idx) => {
            if (idx % tickStep !== 0 && idx !== data.length - 1) return null;
            return (
              <text
                key={`x-${idx}`}
                x={mapX(idx)}
                y={CHART_HEIGHT - PADDING + 18}
                textAnchor="middle"
                fontSize="11"
                fill="#93A0C4"
              >
                {point.label}
              </text>
            );
          })}

        <path d={avgPath} fill="none" stroke="#D9A93B" strokeWidth="2.5" />
        <path d={medPath} fill="none" stroke="#D9A93B" strokeWidth="2.5" strokeDasharray="6 5" />

        {/* Points intentionally omitted for a cleaner line-only look */}

        <text
          x={PADDING - 32}
          y={CHART_HEIGHT / 2}
          fontSize="12"
          fill="#F4F6FB"
          textAnchor="middle"
          transform={`rotate(-90 ${PADDING - 32} ${CHART_HEIGHT / 2})`}
        >
          Average Reaction Time (ms)
        </text>
        <text x={(CHART_WIDTH + PADDING) / 2} y={CHART_HEIGHT - 8} fontSize="12" fill="#F4F6FB">
          Time
        </text>
      </svg>
    </div>
  );
}
